/**
 * @fileoverview Documents tab for ProfileSettings - allows users to upload proof documents
 * Used in two contexts:
 * - onboarding: New users uploading documents for first approval
 * - profile: Active users managing/updating their documents
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { intl, fetchData, fetchUrl, HTTP_STATUS, DmsButton, Notifications } from "dmsbasereact";
import { getBaseURL } from "../../../build_config";
import { downloadFile } from "../../../utils/fileDownload";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Divider,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorIcon from "@mui/icons-material/Error";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import SaveIcon from "@mui/icons-material/Save";
import { DocumentUploadSlot } from "../../../components/DocumentUploadSlot/DocumentUploadSlot";
import { SUGGESTED_DOCUMENT_TIPS } from "../../../constants/UserDocumentRequirements";

/**
 * Status chip colors
 */
const STATUS_COLORS = {
  1: "warning", // PENDING_REVIEW
  2: "success", // APPROVED
  3: "error",   // REJECTED
  4: "default", // EXPIRED
};

/**
 * Status icons
 */
const STATUS_ICONS = {
  1: <HourglassEmptyIcon fontSize="small" />,
  2: <CheckCircleIcon fontSize="small" />,
  3: <ErrorIcon fontSize="small" />,
  4: <ErrorIcon fontSize="small" />,
};

/**
 * Documents tab for uploading and viewing institutional proof documents
 */
export const DocumentsTab = ({ userType = null,
  context = "onboarding",
  onStatusChange = null}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitutionPk, setSelectedInstitutionPk] = useState(null);
  const [institutionApprovals, setInstitutionApprovals] = useState([]);
  const [hasPendingTypeRequest, setHasPendingTypeRequest] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [documentToEdit, setDocumentToEdit] = useState(null);
  const [editDocType, setEditDocType] = useState("");
  const [editing, setEditing] = useState(false);

  // Submit/Save modal state
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Track if documents have been modified (for profile context)
  const [hasChanges, setHasChanges] = useState(false);

  // Get suggested document tips for user type
  const suggestedTips = useMemo(() => SUGGESTED_DOCUMENT_TIPS[userType] || [], [userType]);

  // Filter document types based on user type
  // Institution Responsible users should not see Institution Link Proof (type 1)
  const filteredDocTypes = useMemo(() => {
    if (userType === 'institution_responsible') {
      // Remove Institution Link Proof (type 1) for institution_responsible
      return documentTypes.filter(dtype => dtype.value !== 1);
    }
    return documentTypes;
  }, [documentTypes, userType]);

  // Group documents by institution
  const documentsByInstitution = useMemo(() => {
    if (institutions.length <= 1) return null;

    const grouped = {};
    institutions.forEach(inst => {
      const filteredDocs = documents.filter(doc => doc.organization_branch_pk === inst.organization_branch_pk);
      grouped[inst.organization_branch_pk] = {
        institution: inst,
        documents: filteredDocs
      };
    });
    return grouped;
  }, [documents, institutions]);

  // User can submit if they have at least one document uploaded PER INSTITUTION that requires documents
  // Institutions where user is ACTIVE (status 5) don't require documents
  // Exception: when user has a pending type request, submission is always allowed (even with existing docs)
  const canSubmitDocuments = useMemo(() => {
    if (hasPendingTypeRequest) return documents.length > 0;

    if (institutions.length <= 1) {
      // Single institution: check if already ACTIVE
      if (institutionApprovals.length > 0) {
        const approval = institutionApprovals[0];
        if (approval && approval.status === 5) {
          return true; // Already ACTIVE, no documents needed
        }
      }
      return documents.length > 0;
    }

    // For multiple institutions, only institutions that are NOT ACTIVE must have documents
    const institutionsNeedingDocs = institutions.filter(inst => {
      const approval = institutionApprovals.find(
        app => app.institution_pk === inst.organization_branch_pk
      );
      // If no approval found or status is not ACTIVE (5), documents are required
      return !approval || approval.status !== 5;
    });

    // Can submit if all institutions needing docs have at least one document
    const result = institutionsNeedingDocs.every(inst => {
      const hasDoc = documents.some(doc => {
        return doc.organization_branch_pk === inst.organization_branch_pk;
      });
      return hasDoc;
    });

    return result;
  }, [documents, institutions, institutionApprovals, hasPendingTypeRequest]);

  // Check document status - status 4 is EXPIRED
  const expiredCount = useMemo(() => documents.filter(d => d.status === 4).length, [documents]);

  // Filter institutions to only show those that need documents (not ACTIVE)
  // Exception: when user has a pending type request, show all institutions so they can upload docs
  const institutionsNeedingDocs = useMemo(() => {
    if (hasPendingTypeRequest) return institutions;
    return institutions.filter(inst => {
      const approval = institutionApprovals.find(
        app => app.institution_pk === inst.organization_branch_pk
      );
      // Show institution if no approval found or status is not ACTIVE (5)
      return !approval || approval.status !== 5;
    });
  }, [institutions, institutionApprovals, hasPendingTypeRequest]);

  // Track completion status per institution: which doc types are uploaded and if all are done
  const institutionCompletionStatus = useMemo(() => {
    const status = {};
    institutionsNeedingDocs.forEach(inst => {
      const instDocs = documents.filter(
        doc => doc.organization_branch_pk === inst.organization_branch_pk
      );
      const uploadedTypes = new Set(instDocs.map(doc => doc.document_type));
      const allTypesUploaded = filteredDocTypes.length > 0 &&
        filteredDocTypes.every(dtype => uploadedTypes.has(dtype.value));

      status[inst.institution_pk] = {
        isComplete: allTypesUploaded,
        uploadedTypes,
      };
    });
    return status;
  }, [institutionsNeedingDocs, documents, filteredDocTypes]);

  // Filter doc types to only show those not yet uploaded for the selected institution
  const availableDocTypesForSelected = useMemo(() => {
    if (!selectedInstitutionPk) return filteredDocTypes;
    const completion = institutionCompletionStatus[selectedInstitutionPk];
    if (!completion) return filteredDocTypes;
    return filteredDocTypes.filter(dtype => !completion.uploadedTypes.has(dtype.value));
  }, [filteredDocTypes, selectedInstitutionPk, institutionCompletionStatus]);

  const isSelectedComplete = institutionCompletionStatus[selectedInstitutionPk]?.isComplete || false;

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchData("/account/documents", "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        const docs = response.data?.data || [];
        
        setDocuments(docs);
      } else {
        setError(intl.get("FailedToLoadDocuments").d("Failed to load documents"));
      }
    } catch (err) {
      console.error("Failed to fetch documents:", err);
      setError(intl.get("FailedToLoadDocuments").d("Failed to load documents"));
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch document types on mount
  const fetchDocTypes = useCallback(async () => {
    try {
      const response = await fetchData("/account/document-types", "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        setDocumentTypes(response.data?.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch document types:", err);
    }
  }, []);

  // Fetch user's linked institutions
  const fetchInstitutions = useCallback(async () => {
    try {
      const response = await fetchData("/account/my-institutions", "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        const instData = response.data?.data || [];
        setInstitutions(instData);
        // Set first institution as default selected (use institution_pk for API operations)
        if (instData.length > 0) {
          setSelectedInstitutionPk(instData[0].institution_pk);
        }
      }
    } catch (err) {
      console.error("Failed to fetch institutions:", err);
    }
  }, []);

  const fetchInstitutionApprovals = useCallback(async () => {
    try {
      const response = await fetchData("/account/my-institution-approvals", "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        const approvalsData = response.data?.institutions || [];
        setInstitutionApprovals(approvalsData);
      }
    } catch (err) {
      console.error("Failed to fetch institution approvals:", err);
    }
  }, []);

  const fetchPendingTypeRequests = useCallback(async () => {
    try {
      const response = await fetchData("/person/my_types", "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        const pendingTypes = response.data?.data?.pending_types || [];
        setHasPendingTypeRequest(pendingTypes.length > 0);
      }
    } catch (err) {
      console.error("Failed to fetch pending type requests:", err);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
    fetchDocTypes();
    fetchInstitutions();
    fetchInstitutionApprovals();
    fetchPendingTypeRequests();
  }, [fetchDocuments, fetchDocTypes, fetchInstitutions, fetchInstitutionApprovals, fetchPendingTypeRequests]);

  // Auto-select first institution needing docs when data is loaded
  // Corrects selection if the current institution is ACTIVE, preferring incomplete institutions
  useEffect(() => {
    if (institutionsNeedingDocs.length > 0) {
      const selectedIsValid = institutionsNeedingDocs.some(
        inst => inst.institution_pk === selectedInstitutionPk
      );
      if (!selectedIsValid) {
        const firstIncomplete = institutionsNeedingDocs.find(
          inst => !institutionCompletionStatus[inst.institution_pk]?.isComplete
        );
        setSelectedInstitutionPk(
          firstIncomplete
            ? firstIncomplete.institution_pk
            : institutionsNeedingDocs[0].institution_pk
        );
      }
    }
  }, [institutionsNeedingDocs, institutionCompletionStatus, selectedInstitutionPk]);

  const handleDownload = async (filePk) => {
    try {
      await downloadFile(filePk);
    } catch (err) {
      console.error("Failed to download file:", err);
      Notifications.toast(
        intl.get("DocumentDownloadError").d("Error downloading document"),
        Notifications.TYPE_ERROR
      );
    }
  };

  const handleFileSelect = (docName, file) => {
    setUploadFile(file);
    // Auto-upload when file is selected and doc type is chosen
    if (file && selectedDocType) {
      handleUpload(file, selectedDocType, selectedInstitutionPk);
    }
  };

  const handleDocTypeChange = (e) => {
    const docType = e.target.value;
    setSelectedDocType(docType);
    // Auto-upload if file is already selected
    if (uploadFile && docType) {
      handleUpload(uploadFile, docType, selectedInstitutionPk);
    }
  };

  const handleUpload = async (file, docType, institutionPk = null) => {
    if (!file || !docType) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      // Build URL with institution_pk if provided
      let uploadUrl = `/account/documents/upload?document_type=${docType}`;
      if (institutionPk) {
        uploadUrl += `&institution_pk=${institutionPk}`;
      }

      const response = await fetchUrl(
        uploadUrl,
        'POST',
        true,
        formData,
        false,
        null,
        null,
        null,
        'multipart/form-data',
        false
      );

      if (response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
        Notifications.toast(intl.get("DocumentUploadedSuccessfully").d("Document uploaded successfully"), Notifications.TYPE_SUCCESS);
        setSelectedDocType("");
        setUploadFile(null);
        setHasChanges(true);
        fetchDocuments();
      } else {
        Notifications.toast(response.data?.detail || intl.get("FailedToUploadDocument").d("Failed to upload document"), Notifications.TYPE_ERROR);
      }
    } catch (err) {
      console.error("Failed to upload document:", err);
      Notifications.toast(intl.get("FailedToUploadDocument").d("Failed to upload document"), Notifications.TYPE_ERROR);
    } finally {
      setUploading(false);
    }
  };

  const handleOpenDeleteModal = (doc) => {
    setDocumentToDelete(doc);
    setDeleteReason("");
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
    setDeleteReason("");
  };

  const handleOpenEditModal = (doc) => {
    setDocumentToEdit(doc);
    setEditDocType(doc.document_type);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setDocumentToEdit(null);
    setEditDocType("");
  };

  const handleEditDocument = async () => {
    if (!documentToEdit || !editDocType) {
      Notifications.toast(intl.get("SelectDocumentType").d("Selecione um tipo de documento"), Notifications.TYPE_WARNING);
      return;
    }

    try {
      setEditing(true);
      const response = await fetchData(
        `/account/documents/${documentToEdit.pk}?document_type=${editDocType}`,
        "PUT",
        true
      );

      if (response.status === HTTP_STATUS.OK_200) {
        Notifications.toast(intl.get("DocumentUpdatedSuccessfully").d("Documento atualizado com sucesso"), Notifications.TYPE_SUCCESS);
        handleCloseEditModal();
        setHasChanges(true);
        fetchDocuments();
      } else {
        Notifications.toast(response.data?.detail || intl.get("FailedToUpdateDocument").d("Falha ao atualizar documento"), Notifications.TYPE_ERROR);
      }
    } catch (err) {
      console.error("Failed to update document:", err);
      Notifications.toast(intl.get("FailedToUpdateDocument").d("Falha ao atualizar documento"), Notifications.TYPE_ERROR);
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete || !deleteReason.trim()) {
      Notifications.toast(intl.get("DeleteReasonRequired").d("Please provide a reason for deletion"), Notifications.TYPE_WARNING);
      return;
    }

    try {
      setDeleting(true);
      const response = await fetchData(
        `/account/documents/${documentToDelete.pk}?reason=${encodeURIComponent(deleteReason)}`,
        "DELETE",
        true
      );

      if (response.status === HTTP_STATUS.OK_200) {
        Notifications.toast(intl.get("DocumentDeletedSuccessfully").d("Document deleted successfully"), Notifications.TYPE_SUCCESS);
        handleCloseDeleteModal();
        setHasChanges(true);
        fetchDocuments();
      } else {
        Notifications.toast(response.data?.detail || intl.get("FailedToDeleteDocument").d("Failed to delete document"), Notifications.TYPE_ERROR);
      }
    } catch (err) {
      console.error("Failed to delete document:", err);
      Notifications.toast(intl.get("FailedToDeleteDocument").d("Failed to delete document"), Notifications.TYPE_ERROR);
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      setSubmitting(true);
      // Pass userType as query parameter for multi-type users
      const url = userType
        ? `/account/documents/submit-for-approval?user_type=${userType}`
        : "/account/documents/submit-for-approval";
      const response = await fetchData(url, "POST", true);

      if (response.status === HTTP_STATUS.OK_200) {
        Notifications.toast(intl.get("DocumentsSubmittedSuccessfully").d("Documents submitted for approval successfully"), Notifications.TYPE_SUCCESS);
        setSubmitModalOpen(false);
        setHasChanges(false);
        if (onStatusChange) {
          onStatusChange();
        }
      } else {
        Notifications.toast(response.data?.detail || intl.get("FailedToSubmitDocuments").d("Failed to submit documents"), Notifications.TYPE_ERROR);
      }
    } catch (err) {
      console.error("Failed to submit documents:", err);
      Notifications.toast(intl.get("FailedToSubmitDocuments").d("Failed to submit documents"), Notifications.TYPE_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setSubmitting(true);
      const response = await fetchData("/account/documents/request-review", "POST", true);

      if (response.status === HTTP_STATUS.OK_200) {
        Notifications.toast(intl.get("DocumentChangesSaved").d("Changes saved. Your account will be reviewed by the management team."), Notifications.TYPE_SUCCESS);
        setSubmitModalOpen(false);
        setHasChanges(false);
        if (onStatusChange) {
          onStatusChange();
        }
      } else {
        Notifications.toast(response.data?.detail || intl.get("FailedToSaveChanges").d("Failed to save changes"), Notifications.TYPE_ERROR);
      }
    } catch (err) {
      console.error("Failed to save changes:", err);
      Notifications.toast(intl.get("FailedToSaveChanges").d("Failed to save changes"), Notifications.TYPE_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  // Determine button state based on context
  const isOnboarding = context === "onboarding";
  const canSubmit = canSubmitDocuments;
  const showSubmitButton = isOnboarding || hasChanges || hasPendingTypeRequest;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">
          {intl.get("MyDocuments").d("My Documents")}
        </Typography>
      </Box>

      {/* Status alerts */}
      {expiredCount > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {intl.get("ExpiredDocumentsWarning", { count: expiredCount }).d("You have {count} expired document(s) that need to be renewed")}
        </Alert>
      )}

      {!isOnboarding && hasChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {intl.get("DocumentChangesWarning").d("You have made changes to your documents. Save to submit for review by the management team.")}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Show pending type request info when user is adding a new profile type */}
      {hasPendingTypeRequest && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {intl.get("PendingTypeRequestDocuments").d("You have a new profile type pending approval. You can upload or update your documents for review.")}
        </Alert>
      )}

      {/* Show success message if all institutions are ACTIVE and no pending type request */}
      {institutionsNeedingDocs.length === 0 && institutions.length > 0 && !hasPendingTypeRequest && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {intl.get("AllInstitutionsApproved").d("You are already approved at all your institutions. No documents required.")}
        </Alert>
      )}

      {/* Upload area - inline (only show if there are institutions needing docs) */}
      {institutionsNeedingDocs.length > 0 && (
      <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {intl.get("UploadNewDocument").d("Upload New Document")}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" }, flexWrap: "wrap" }}>
          {institutions.length > 1 && institutionsNeedingDocs.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{intl.get("Institution").d("Institution")}</InputLabel>
              <Select
                value={selectedInstitutionPk || ""}
                label={intl.get("Institution").d("Institution")}
                onChange={(e) => {
                  setSelectedInstitutionPk(e.target.value);
                  setSelectedDocType("");
                }}
                disabled={uploading}
              >
                {institutionsNeedingDocs.map((inst) => (
                  <MenuItem key={inst.institution_pk} value={inst.institution_pk}>
                    {inst.institution_name}
                    {institutionCompletionStatus[inst.institution_pk]?.isComplete
                      ? " - Todos os documentos submetidos"
                      : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={isSelectedComplete}>
            <InputLabel>{intl.get("DocumentType").d("Document Type")}</InputLabel>
            <Select
              value={selectedDocType}
              label={intl.get("DocumentType").d("Document Type")}
              onChange={handleDocTypeChange}
              disabled={uploading || isSelectedComplete}
            >
              {availableDocTypesForSelected.map((dtype) => (
                <MenuItem key={dtype.value} value={dtype.value}>
                  {dtype.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <DocumentUploadSlot
              docName={intl.get("SelectFile").d("Select File")}
              file={uploadFile}
              onFileSelect={handleFileSelect}
              disabled={uploading || !selectedDocType || (institutions.length > 1 && !selectedInstitutionPk) || isSelectedComplete}
            />
          </Box>
        </Box>
        {uploading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              {intl.get("Uploading").d("Uploading...")}
            </Typography>
          </Box>
        )}
      </Box>
      )}

      {/* Uploaded documents list */}
      {documents.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          {institutions.length > 1 && documentsByInstitution ? (
            // Show documents grouped by institution
            <>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {intl.get("DocumentsByInstitution").d("Documents by Institution")}
              </Typography>
              {institutions.map((inst) => {
                const instDocs = documentsByInstitution[inst.organization_branch_pk]?.documents || [];
                const approval = institutionApprovals.find(app => app.organization_branch_pk === inst.organization_branch_pk);
                const isApproved = approval && approval.status === 5; // ACTIVE

                return (
                  <Box key={inst.organization_branch_pk} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "primary.main" }}>
                        {inst.institution_name}
                      </Typography>
                      {isApproved && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={intl.get("Approved").d("Approved")}
                          size="small"
                          color="success"
                        />
                      )}
                    </Box>
                    {isApproved ? (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        {intl.get("AlreadyApprovedAtInstitution").d("You are already approved at this institution. No documents required.")}
                      </Alert>
                    ) : instDocs.length === 0 ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        {intl.get("NoDocumentsForInstitution").d("No documents uploaded for this institution yet")}
                      </Alert>
                    ) : (
                      <List disablePadding>
                        {instDocs.map((doc) => (
                          <ListItem
                            key={doc.pk}
                            sx={{
                              bgcolor: "background.paper",
                              borderRadius: 1,
                              mb: 1,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <ListItemIcon>
                              <DescriptionIcon color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary={doc.document_type_label}
                              secondary={
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                  <Chip
                                    size="small"
                                    label={doc.status_label}
                                    color={STATUS_COLORS[doc.status] || "default"}
                                    icon={STATUS_ICONS[doc.status]}
                                    sx={{ width: "fit-content" }}
                                  />
                                  {doc.review_notes && (
                                    <Typography variant="caption" color="error">
                                      {intl.get("RejectionReason").d("Rejection Reason")}: {doc.review_notes}
                                    </Typography>
                                  )}
                                  {doc.created_on && (
                                    <Typography variant="caption" color="text.secondary">
                                      {intl.get("UploadedOn").d("Uploaded on")}: {new Date(doc.created_on).toLocaleDateString('pt-BR')}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              secondaryTypographyProps={{ component: "div" }}
                            />
                            <ListItemSecondaryAction>
                              <Tooltip title={intl.get("Tooltip.Action.Download").d("Baixar")}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownload(doc.file_pk)}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>
                              {(doc.status === 1 || hasPendingTypeRequest) && (
                                <Tooltip title={intl.get("Tooltip.Action.Edit").d("Edit")}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenEditModal(doc)}
                                    color="primary"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title={intl.get("Tooltip.Action.Delete").d("Excluir")}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDeleteModal(doc)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                );
              })}
            </>
          ) : (
            // Show all documents in a single list (single institution or no institutions)
            <>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {intl.get("UploadedDocuments").d("Uploaded Documents")} ({documents.length})
              </Typography>
              <List disablePadding>
                {documents.map((doc) => (
                  <ListItem
                    key={doc.pk}
                    sx={{
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      mb: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <ListItemIcon>
                      <DescriptionIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={doc.document_type_label}
                      secondary={
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Chip
                            size="small"
                            label={doc.status_label}
                            color={STATUS_COLORS[doc.status] || "default"}
                            icon={STATUS_ICONS[doc.status]}
                            sx={{ width: "fit-content" }}
                          />
                          {doc.review_notes && (
                            <Typography variant="caption" color="error">
                              {intl.get("RejectionReason").d("Rejection Reason")}: {doc.review_notes}
                            </Typography>
                          )}
                          {doc.created_on && (
                            <Typography variant="caption" color="text.secondary">
                              {intl.get("UploadedOn").d("Uploaded on")}: {new Date(doc.created_on).toLocaleDateString('pt-BR')}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondaryTypographyProps={{ component: "div" }}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={intl.get("Tooltip.Action.Download").d("Baixar")}>
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(doc.file_pk)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      {doc.status === 1 && (
                        <Tooltip title={intl.get("Tooltip.Action.Edit").d("Edit")}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditModal(doc)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={intl.get("Tooltip.Action.Delete").d("Excluir")}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteModal(doc)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </>
      )}

      {documents.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {intl.get("NoDocumentsUploaded").d("No documents uploaded")}
        </Alert>
      )}

      {/* Submit/Save button - at the bottom right */}
      {showSubmitButton && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Tooltip
            title={!canSubmit ? intl.get("UploadAtLeastOneDoc").d("Upload at least one document to continue") : ""}
            placement="top"
          >
            <span>
              <DmsButton
                variant="success"
                text={isOnboarding
                  ? intl.get("SubmitForApproval").d("Submit for Approval")
                  : intl.get("SaveChanges").d("Save Changes")
                }
                onClick={() => setSubmitModalOpen(true)}
                icon={isOnboarding ? <SendIcon /> : <SaveIcon />}
                disabled={!canSubmit}
              />
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Delete Document Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="sm" fullWidth>
        <DialogTitle>{intl.get("DeleteDocument").d("Delete Document")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Alert severity="warning">
              {intl.get("DeleteDocumentWarning").d("This action cannot be undone. Please provide a reason for deleting this document.")}
            </Alert>
            {documentToDelete && (
              <Typography variant="body2">
                <strong>{intl.get("Document").d("Document")}:</strong> {documentToDelete.document_type_label}
              </Typography>
            )}
            <TextField
              fullWidth
              multiline
              rows={3}
              label={intl.get("DeleteReason").d("Deletion reason")}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder={intl.get("DeleteReasonPlaceholder").d("Explain why you are deleting this document...")}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <DmsButton
            variant="secondary"
            text={intl.get("Cancel").d("Cancel")}
            onClick={handleCloseDeleteModal}
            disabled={deleting}
          />
          <DmsButton
            variant="danger"
            text={deleting ? intl.get("Deleting").d("Deleting...") : intl.get("Delete").d("Delete")}
            onClick={handleDeleteDocument}
            disabled={deleting || !deleteReason.trim()}
          />
        </DialogActions>
      </Dialog>

      {/* Edit Document Modal */}
      <Dialog open={editModalOpen} onClose={handleCloseEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>{intl.get("EditDocumentType").d("Edit Document Type")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {documentToEdit && (
              <Typography variant="body2" color="text.secondary">
                {intl.get("CurrentDocumentType").d("Current type")}: <strong>{documentToEdit.document_type_label}</strong>
              </Typography>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>{intl.get("NewDocumentType").d("New Document Type")}</InputLabel>
              <Select
                value={editDocType}
                label={intl.get("NewDocumentType").d("New Document Type")}
                onChange={(e) => setEditDocType(e.target.value)}
                disabled={editing}
              >
                {filteredDocTypes.map((dtype) => (
                  <MenuItem key={dtype.value} value={dtype.value}>
                    {dtype.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <DmsButton
            variant="secondary"
            text={intl.get("Cancel").d("Cancel")}
            onClick={handleCloseEditModal}
            disabled={editing}
          />
          <DmsButton
            variant="primary"
            text={editing ? intl.get("Saving").d("Saving...") : intl.get("Save").d("Save")}
            onClick={handleEditDocument}
            disabled={editing || !editDocType || editDocType === documentToEdit?.document_type}
          />
        </DialogActions>
      </Dialog>

      {/* Submit/Save Confirmation Modal */}
      <Dialog open={submitModalOpen} onClose={() => setSubmitModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isOnboarding
            ? intl.get("SubmitForApproval").d("Submit for Approval")
            : intl.get("SaveDocumentChanges").d("Save Document Changes")
          }
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Alert severity={isOnboarding ? "info" : "warning"}>
              {isOnboarding
                ? intl.get("SubmitForApprovalWarning").d("You are about to submit your documents for analysis. The management team will verify if the documents are valid.")
                : intl.get("SaveChangesWarning").d("Your changes will be submitted for review by the management team. Your access may be affected during the review.")
              }
            </Alert>
            <Typography variant="body2">
              <strong>{intl.get("DocumentsUploaded").d("Documents uploaded")}:</strong> {documents.length}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <DmsButton
            variant="secondary"
            text={intl.get("Cancel").d("Cancel")}
            onClick={() => setSubmitModalOpen(false)}
            disabled={submitting}
          />
          <DmsButton
            variant="success"
            text={submitting
              ? intl.get("Submitting").d("Submitting...")
              : intl.get("Confirm").d("Confirm")
            }
            onClick={isOnboarding ? handleSubmitForApproval : handleSaveChanges}
            disabled={submitting}
            icon={isOnboarding ? <SendIcon /> : <SaveIcon />}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

DocumentsTab.propTypes = {
  userType: PropTypes.string,
  context: PropTypes.oneOf(["onboarding", "profile"]),
  onStatusChange: PropTypes.func,
};

